"use client";
import Image from "next/image";
import { useEffect, useState } from "react";
import { SITE } from "@/lib/seo/site";
import { pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";
import { cn } from "@/lib/utils";
import { APP_BANNER_DISMISSED_KEY } from "@/components/layout/AppDownloadBanner";
import { isMaintenanceActive } from "@/components/maintenance/flag";
import { SAME_ORIGIN_LOGO } from "@/components/layout/brand-assets";

// Auto-open the sheet once on mobile when the user has scrolled enough to
// signal engagement — mirrors the Angular reference's scroll counter
// (mousewheel ≥ 2 / touchmove ≥ 10). Only fires once per page load.
const MOBILE_BREAKPOINT_PX = 960;
const SCROLL_TRIGGER_Y_PX = 300;

const PLAY_STORE =
  "https://play.google.com/store/apps/details?id=com.hqinfosystem.sticko.wastickerapps.stickers&referrer=utm_source%3Dweb%26utm_medium%3Dweb%26utm_content%3Dimageclick%26utm_campaign%3Dhomepage";
const APP_STORE = "https://apps.apple.com/in/app/sticko-animated-sticker-maker/id1592890261";

const BROWSER_ICONS: Array<{ test: RegExp; file: string; label: string }> = [
  // Order matters: Edge & Brave & Opera contain "Chrome" too, so they go first.
  { test: /edg\//i, file: "edge.png", label: "Microsoft Edge" },
  { test: /opr\/|opera/i, file: "opera-mini.png", label: "Opera" },
  { test: /brave/i, file: "brave.png", label: "Brave" },
  { test: /ucbrowser/i, file: "uc.png", label: "UC Browser" },
  { test: /firefox/i, file: "firefox.png", label: "Firefox" },
  { test: /chrome/i, file: "chrome.png", label: "Google Chrome" },
  { test: /safari/i, file: "safari.png", label: "Safari" },
];

function detectBrowserIcon(): string {
  if (typeof window === "undefined") return "no-browser-icon.png";
  const ua = window.navigator.userAgent;
  for (const { test, file } of BROWSER_ICONS) {
    if (test.test(ua)) return file;
  }
  return "no-browser-icon.png";
}

function detectStoreUrl(): string {
  if (typeof window === "undefined") return PLAY_STORE;
  const ua = window.navigator.userAgent;
  if (/iphone|ipad|ipod|mac os/i.test(ua)) return APP_STORE;
  return PLAY_STORE;
}

/**
 * Opens the app-promo bottom sheet from anywhere via a CustomEvent — keeps
 * triggers (Like / Download icons inside server-rendered cards) free of the
 * sheet's React state. Listeners live only in this component.
 */
export const APP_PROMPT_EVENT = "sticko:open-app-prompt";

export function openAppPrompt() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(APP_PROMPT_EVENT));
}

export function AppPromptSheet() {
  const [open, setOpen] = useState(false);
  const [browserIcon, setBrowserIcon] = useState("no-browser-icon.png");

  useEffect(() => {
    const onOpen = () => {
      // The maintenance dialog owns the screen — nothing else may appear over it.
      if (isMaintenanceActive()) return;
      setBrowserIcon(detectBrowserIcon());
      setOpen(true);
    };
    window.addEventListener(APP_PROMPT_EVENT, onOpen);
    return () => window.removeEventListener(APP_PROMPT_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isMaintenanceActive()) return;
    let triggered = false;
    const onScroll = () => {
      if (triggered) return;
      if (window.innerWidth >= MOBILE_BREAKPOINT_PX) return;
      if (window.scrollY < SCROLL_TRIGGER_Y_PX) return;
      // Re-check on each fire so the X-button dismissal from the banner
      // suppresses the sheet without needing a cross-component event bus.
      if (window.localStorage.getItem(APP_BANNER_DISMISSED_KEY) === "1") {
        triggered = true;
        return;
      }
      triggered = true;
      setBrowserIcon(detectBrowserIcon());
      setOpen(true);
      pushEvent(analyticsEvents.app_prompt_open, { intent: "scroll" });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  function handleDownload() {
    pushEvent(analyticsEvents.app_prompt_cta_click, { action: "open-app" });
    pushEvent(analyticsEvents.app_install_click, { source: "bottom-sheet" });
    window.location.href = detectStoreUrl();
  }

  const browserLabel = BROWSER_ICONS.find((b) => b.file === browserIcon)?.label ?? "Web browser";

  return (
    <>
      {/* Backdrop is mounted only while the sheet is open. A permanently
          present `fixed inset-0` overlay — even transparent and
          pointer-events-none — makes AdSense Auto Ads treat the whole
          viewport as covered, so it never places the side rails (and pulls
          any it did place on its scroll-time collision re-check). */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={() => {
            pushEvent(analyticsEvents.app_prompt_cta_click, { action: "dismiss" });
            setOpen(false);
          }}
          aria-hidden
        />
      )}
      <div
        role="dialog"
        data-track-zone="app-prompt"
        aria-label="Get the Sticko mobile app"
        // `inert` (not aria-hidden) while closed: the sheet stays mounted
        // below the viewport, so aria-hidden alone would leave its buttons
        // focusable in the tab order — the aria-hidden-focus accessibility
        // failure. `inert` drops the subtree from the tab order and the
        // accessibility tree until the sheet opens.
        inert={!open}
        className={cn(
          // Anchored to the bottom edge only — never inset-0 — so when
          // closed (translate-y-full parks it fully below the viewport) it
          // occupies zero on-screen area and can't block Auto Ads rails.
          "fixed inset-x-0 bottom-0 z-50 w-full rounded-t-2xl bg-white shadow-(--shadow-card) transition-transform duration-300",
          open ? "translate-y-0" : "pointer-events-none translate-y-full",
        )}
      >
        <div className="px-3 pt-3">
          <div
            className="rounded-card flex items-center gap-3 px-4 py-3 text-white"
            style={{ background: "linear-gradient(90deg, #7366f9, #6600ff)" }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={SAME_ORIGIN_LOGO} alt="Sticko" width={40} height={40} className="shrink-0" />
            <p className="flex-1 text-[13px] leading-tight">
              Download our Mobile App for better experience. Best WhatsApp Sticker Making App ever
            </p>
            <button
              type="button"
              onClick={handleDownload}
              className="rounded-pill text-brand-700 shrink-0 bg-white px-4 py-1.5 text-[13px] font-semibold hover:bg-white/90"
            >
              Download
            </button>
          </div>
        </div>

        <div>
          <div className="border-border flex items-center gap-4 border-b px-4 py-3">
            {/* Conditional render — loading="lazy" alone is insufficient
                because the sheet's layout box is anchored to the viewport
                bottom (offscreen via translate-y-full, but the layout box
                stays inside the lazy-load rootMargin). Without gating on
                `open` Chrome fetches this ~640KB asset during initial page
                load, starving Roboto and the above-fold sticker thumbs. */}
            {open ? (
              /* eslint-disable-next-line @next/next/no-img-element */
              <img
                src={`${SITE.cdn}/static-content/sticko-app-icon.svg`}
                alt="Sticko app icon"
                width={46}
                height={46}
                decoding="async"
                className="shrink-0"
              />
            ) : null}
            <p className="text-foreground flex-1 text-[14px] font-medium">For better experience</p>
            <button
              type="button"
              onClick={handleDownload}
              className="rounded-pill bg-brand-500 hover:bg-brand-600 shrink-0 px-4 py-1.5 text-[13px] font-semibold text-white"
            >
              Open App
            </button>
          </div>

          <div className="flex items-center gap-4 px-4 py-3">
            <span className="border-border relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border bg-white">
              {open ? (
                <Image
                  src={`${SITE.cdn}/static-content/${browserIcon}`}
                  alt={browserLabel}
                  width={40}
                  height={40}
                  unoptimized
                  className="object-contain"
                />
              ) : null}
            </span>
            <p className="text-foreground flex-1 text-[14px] font-medium">Continue in browser</p>
            <button
              type="button"
              onClick={() => {
                pushEvent(analyticsEvents.app_prompt_cta_click, {
                  action: "continue-in-browser",
                });
                setOpen(false);
              }}
              className="rounded-pill border-border bg-muted text-muted-foreground hover:bg-muted/80 shrink-0 border px-4 py-1.5 text-[13px] font-semibold"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
